import React from 'react';

import { InputGroup, Spinner } from 'react-bootstrap';

import { translateComponent } from '@/utils/tolgee/translateComponent';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-bootstrap-typeahead/css/Typeahead.bs5.css';

interface GenericTypeaheadProps<T> {
  /** API endpoint to fetch search results */
  endpoint: string;
  /** Field (or function) to use as display label */
  labelKey: keyof T | ((option: T) => string);
  /** Placeholder text */
  placeholder?: string;
  /** Minimum characters before search triggers */
  minLength?: number;
  /** Callback when an option is selected */
  onSelect: (item: T) => void;
  /** Optional custom render for menu items */
  renderItem?: (option: T, state: any) => React.ReactNode;
  submitting?: boolean;
  clearTrigger?: number;
}

/**
 * Molecules: LomavisTypeahead
 */
export function LomavisTypeahead<T extends { id?: string | number }>({
  endpoint,
  labelKey,
  placeholder = 'Search...',
  minLength = 2,
  onSelect,
  renderItem,
  submitting,
  clearTrigger
}: GenericTypeaheadProps<T>) {
  const t = translateComponent('LomavisTypeahead');
  const [options, setOptions] = React.useState<T[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [selected, setSelected] = React.useState<T[]>([]);
  const debounceRef = React.useRef<NodeJS.Timeout | null>(null);
  const typeaheadRef = React.useRef<any>(null);

  // Clear input when clearTrigger changes
  React.useEffect(() => {
    if (clearTrigger !== undefined) {
      setSelected([]);
      setOptions([]);
      if (typeaheadRef.current) {
        typeaheadRef.current.clear();
      }
    }
  }, [clearTrigger]);

  const search = (query: string) => {
    if (!query || query.length < minLength) {
      setOptions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const res = await fetch(`${endpoint}?q=${encodeURIComponent(query)}&max_results=20`);
          const data = await res.json();
          setOptions(data || []);
        } catch (err) {
          console.error('Typeahead fetch error:', err);
          setOptions([]);
        } finally {
          setIsLoading(false);
        }
      };

      fetchData().catch((err) => {
        console.error('Unhandled fetchData error:', err);
      });
    }, 400);
  };

  return (
    <div>
      <Typeahead
        ref={typeaheadRef}
        id="lomavis-typeahead"
        labelKey={labelKey as any}
        onInputChange={search}
        options={options}
        placeholder={placeholder || t('search_placeholder')}
        isLoading={isLoading}
        filterBy={() => true}
        selected={selected}
        onChange={(selectedOptions) => {
          setSelected(selectedOptions as T[]);
          const first = selectedOptions?.[0];
          if (first) {
            onSelect(first as T);
          }
        }}
        clearButton
        emptyLabel={
          isLoading ? (
            <div className="w-100 d-flex justify-content-center">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            t('no_results')
          )
        }
        renderInput={({ inputRef, referenceElementRef, ...inputProps }) => (
          <InputGroup>
            <InputGroup.Text>
              {submitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <i className="fa-regular fa-magnifying-glass fs-5"></i>
              )}
            </InputGroup.Text>
            <input
              {...inputProps}
              ref={(node) => {
                inputRef(node);
                referenceElementRef(node);
              }}
              className="form-control"
            />
          </InputGroup>
        )}
        renderMenuItemChildren={(option, state) =>
          renderItem ? (
            <>{renderItem(option as T, state)}</>
          ) : (
            <div className="px-2 py-1">
              <span>
                {typeof labelKey === 'function'
                  ? labelKey(option as T)
                  : String((option as any)[labelKey])}
              </span>
            </div>
          )
        }
      />
    </div>
  );
}
